import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState, Suspense, lazy } from "react";
import sessionManager from "./api/SessionManager";
import ProtectedRoute from './components/ProtectedRoute';
import NotificationPermission from './components/notifications/NotificationPermission';

// Lazy load heavy components
const Home = lazy(() => import("./Home"));
const PlayerDashboard = lazy(() => import("./PlayerDashboard"));
const PlayerProfile = lazy(() => import("./PlayerProfile"));
const GoogleCallback = lazy(() => import('./GoogleCallback'));
const CreateTeam = lazy(() => import('./components/team/CreateTeam'));
const TeamManagement = lazy(() => import('./components/team/TeamManagement'));
const PublicTournaments = lazy(() => import('./PublicTournaments'));

const App = () => {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status when app loads
  useEffect(() => {
    const checkAuth = async () => {
      await sessionManager.validateSession();
      setIsAuthenticated(sessionManager.isUserAuthenticated());
      setAuthChecked(true);
    };
    
    checkAuth();
    
    // Subscribe to auth state changes
    const authStateListener = (isAuth) => {
      setIsAuthenticated(isAuth);
    };
    
    sessionManager.addAuthStateListener(authStateListener);
    
    return () => {
      sessionManager.removeAuthStateListener(authStateListener);
    };
  }, []);

  // Show loading state while checking auth
  if (!authChecked) {
    return <div className="h-screen w-full flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>;
  }

  return (
    <Router>
      {/* Only show notification permission for authenticated users */}
      {isAuthenticated && <NotificationPermission />}

      <Suspense fallback={
        <div className="h-screen w-full flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      }>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Home />} />
          <Route path="/signup" element={<Home />} />
          <Route path="/tournaments" element={<PublicTournaments />} />
          <Route path="/google/callback" element={<GoogleCallback />} />
          <Route
            path="/playerdashboard"
            element={<ProtectedRoute isAuthenticated={isAuthenticated} component={PlayerDashboard} />}
          />
          <Route
            path="/player/profile"
            element={<ProtectedRoute isAuthenticated={isAuthenticated} component={PlayerProfile} />}
          />
          <Route path="/player/teams/create" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <CreateTeam />
            </ProtectedRoute>
          } />
          <Route path="/player/teams/:teamId/manage" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <TeamManagement />
            </ProtectedRoute>
          } />
          {/* Make sure this catch-all route is at the end */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
